import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  TextField,
  Button,
  Container,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Avatar,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { CloudUpload as CloudUploadIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useAuth } from '../context/auth';

const EmployeeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const isEditMode = Boolean(id);

  const baseUrl = process.env.REACT_APP_BASE_URL;

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    emergencyContactNumber: '',
    emergencyContactName: '',
    dateOfHire: '',
    jobTitle: '',
    educationalBackground: '',
    countryOfOrigin: 'Liberia',
    district: '',
    county: '',
    address: '',
    maritalStatus: '',
    tor: '',
  });

  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Counties in Liberia
  const liberianCounties = [
    'Bomi', 'Bong', 'Gbarpolu', 'Grand Bassa', 'Grand Cape Mount',
    'Grand Gedeh', 'Grand Kru', 'Lofa', 'Margibi', 'Maryland',
    'Montserrado', 'Nimba', 'River Cess', 'River Gee', 'Sinoe'
  ];

  const maritalStatuses = ['Single', 'Married', 'Divorced', 'Widowed'];

  useEffect(() => {
    if (isEditMode) {
      fetchEmployee();
    }
  }, [id]);

  const fetchEmployee = async () => {
    setFetchLoading(true);
    try {
      const response = await axios.get(`${baseUrl}/api/employees/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const employee = response.data.employee;
      setFormData({
        firstName: employee.firstName || '',
        middleName: employee.middleName || '',
        lastName: employee.lastName || '',
        phoneNumber: employee.phoneNumber || '',
        email: employee.email || '',
        emergencyContactNumber: employee.emergencyContactNumber || '',
        emergencyContactName: employee.emergencyContactName || '',
        dateOfHire: employee.dateOfHire || '',
        jobTitle: employee.jobTitle || '',
        educationalBackground: employee.educationalBackground || '',
        countryOfOrigin: employee.countryOfOrigin || 'Liberia',
        district: employee.district || '',
        county: employee.county || '',
        address: employee.address || '',
        maritalStatus: employee.maritalStatus || '',
        tor: employee.tor || '',
      });

      if (employee.profilePicture) {
        setProfilePicturePreview(`${baseUrl}${employee.profilePicture}`);
      }
    } catch (error) {
      console.error('Error fetching employee:', error);
      setError(error.response?.data?.error || 'Failed to load employee data');
    } finally {
      setFetchLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }

      setProfilePicture(file);
      setProfilePicturePreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate required fields
      if (!formData.firstName || !formData.lastName || !formData.phoneNumber ||
          !formData.email || !formData.dateOfHire || !formData.jobTitle) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      // Create FormData for multipart/form-data
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          data.append(key, formData[key]);
        }
      });

      if (profilePicture) {
        data.append('profilePicture', profilePicture);
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
        }
      };

      if (isEditMode) {
        await axios.put(`${baseUrl}/api/employees/${id}`, data, config);
        setSuccess('Employee updated successfully!');
      } else {
        await axios.post(`${baseUrl}/api/employees`, data, config);
        setSuccess('Employee created successfully!');
      }

      setTimeout(() => {
        navigate('/dashboard/employees');
      }, 1500);
    } catch (error) {
      console.error('Error saving employee:', error);
      setError(error.response?.data?.error || 'Failed to save employee. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard/employees')}
          variant="outlined"
        >
          Back
        </Button>
        <Typography variant="h4" component="h1">
          {isEditMode ? 'Edit Employee' : 'Add New Employee'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Paper elevation={3} sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          {/* Profile Picture */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Avatar
              src={profilePicturePreview}
              sx={{ width: 120, height: 120, margin: '0 auto', mb: 2 }}
            >
              {formData.firstName?.[0]}{formData.lastName?.[0]}
            </Avatar>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
            >
              Upload Profile Picture
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleFileChange}
              />
            </Button>
          </Box>

          {/* Personal Information */}
          <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
            Personal Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Middle Name"
                name="middleName"
                value={formData.middleName}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>

          {/* Contact Information */}
          <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
            Contact Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Phone Number"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Emergency Contact Name"
                name="emergencyContactName"
                value={formData.emergencyContactName}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Emergency Contact Number"
                name="emergencyContactNumber"
                value={formData.emergencyContactNumber}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>

          {/* Employment Information */}
          <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
            Employment Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Job Title"
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Date of Hire"
                name="dateOfHire"
                type="date"
                value={formData.dateOfHire}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Educational Background"
                name="educationalBackground"
                value={formData.educationalBackground}
                onChange={handleInputChange}
                placeholder="e.g., Bachelor's Degree in Computer Science"
              />
            </Grid>
          </Grid>

          {/* Location Information */}
          <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
            Location Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Country of Origin"
                name="countryOfOrigin"
                value={formData.countryOfOrigin}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>County</InputLabel>
                <Select
                  name="county"
                  value={formData.county}
                  onChange={handleInputChange}
                  label="County"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {liberianCounties.map((county) => (
                    <MenuItem key={county} value={county}>
                      {county}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="District"
                name="district"
                value={formData.district}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>

          {/* Other Information */}
          <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>
            Other Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Marital Status</InputLabel>
                <Select
                  name="maritalStatus"
                  value={formData.maritalStatus}
                  onChange={handleInputChange}
                  label="Marital Status"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {maritalStatuses.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Terms of Reference (TOR)"
                name="tor"
                value={formData.tor}
                onChange={handleInputChange}
                multiline
                rows={4}
                placeholder="Enter employee's terms of reference, responsibilities, and duties..."
              />
            </Grid>
          </Grid>

          {/* Submit Button */}
          <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard/employees')}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : (isEditMode ? 'Update Employee' : 'Create Employee')}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default EmployeeForm;
